import * as C from '../common/constants';
import Dialog from '@mui/material/Dialog';
import MuiDialogContent from '@mui/material/DialogContent';
import React, { useState, useCallback, useEffect } from 'react';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { ReactComponent as CloseIcon } from '../assets/svg/cancel_circle.svg';
import { getText } from '../common/utils/helpers';
import { useSelector, useDispatch } from 'react-redux';
import * as ROUTES from '../common/routes';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { StyleRules, createStyles, makeStyles } from '@mui/styles';
import Button from './Button';
import { storeDraftsData } from '../common/app/actions';
import { db } from '../config';
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  setDoc,
  getDoc,
} from 'firebase/firestore';

interface IProps {
  onClose: () => void;
}

const useStyles = makeStyles(
  (): StyleRules =>
    createStyles({
      dialogContainerStyle: {
        '& > div > div': {
          maxWidth: `600px`,
          width: '100%',
          background: COLORS.white,
        },
      },
      closeButtonStyle: {
        position: 'absolute',
        right: 20, // theme.spacing(2),
        top: 20, // theme.spacing(2),
        fill: COLORS.textGray,
        width: '30px',
        height: '30px',
        pointerEvents: 'auto',
        zIndex: 100,
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: 0.7,
            transition: '0.5s ease',
          },
        },
      },
      dialogContentStyle: {
        // margin: '16px',
        color: COLORS.textBlack,
      },
      titleStyle: {
        width: '100%',
        fontFamily: FONTS.garamond,
        fontWeight: FONT_WEIGHT.medium,
        color: COLORS.textBlack,
        textAlign: 'center',
        textTransform: 'uppercase',
        fontSize: '20px',
        margin: '10px 0 30px 0',
      },
      spanStyle: {
        marginBottom: '20px',
      },
      buttonsContainerStyle: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'row',
        width: '100%',
      },
      fieldsContainerStyle: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      },
      themeStyle: {
        fontSize: '20px',
        marginBottom: '18px',
        fontWeight: FONT_WEIGHT.medium,
        fontFamily: FONTS.alegreya,
        color: COLORS.appBlue,
        whiteSpace: 'nowrap',
        display: 'flex',
        flexDirection: 'column',
      },
      surveyStyle: {
        fontWeight: FONT_WEIGHT.medium,
        fontFamily: FONTS.alegreya,
        color: COLORS.appBlue,
        fontSize: '18px',
        textTransform: 'capitalize',
        margin: '10px 0 6px 20px',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            color: COLORS.black,
            transition: '0.5s ease',
          },
        },
      },
    })
);

const useArrayRef = () => {
  const refs = [];
  return [refs, (el) => el && refs.push(el)];
};

const DraftsDialog = ({ onClose }: IProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const lng = useSelector((state: any) => state.app.lng);
  const draftsData = useSelector((state: any) => state.app.draftsData);
  const authData = useSelector((state: any) => state.app.authData);

  const userRef = doc(db, 'users', authData?.uid);

  const getFirestoreData = async () => {
    const docSnap = await getDoc(userRef);
    const drafts = docSnap.data()?.drafts;

    if (drafts) {
      dispatch(storeDraftsData(drafts));
    }
  };

  useEffect(() => {
    if (authData) {
      getFirestoreData();
    }
  }, [authData]);

  const isMobile = window.innerWidth < 900;

  // const onSave = async (event: React.SyntheticEvent) => {
  //   event.stopPropagation();
  //   event.preventDefault();

  //   setIsLoading(true);

  //   const formData: any = {};

  //   (elements as any[]).forEach((elementItem) => {
  //     // console.log('>>>>111', elementItem);
  //     const elementKey = elementItem?.getAttribute('data-name');
  //     // const inputType = elementItem?.getAttribute('data-type');

  //     formData[elementKey] = elementItem?.value;
  //   });

  //   try {
  //     await setDoc(userRef, {
  //       profile: formData,
  //     });

  //     dispatch(storeProfileData(formData));
  //   } catch (error) {
  //     console.log(error);
  //   } finally {
  //     setIsLoading(false);
  //     onClose();
  //   }

  //   // console.log('>>>>', id);
  // };

  const onClick = (event: React.SyntheticEvent, themeId, surveyId) => {
    event.stopPropagation();
    event.preventDefault();

    navigate({
      pathname: `${ROUTES.ADD}/${themeId}/${surveyId}`,
      search,
    });
  };

  const getSurvey = (surveyId, index, themeData, themeId) => {
    const rawSurveyData = themeData[surveyId];
    const surveyData = JSON.parse(rawSurveyData);
    const label = surveyData?.label;

    return (
      <div
        className={classes.surveyStyle}
        key={index}
        onClick={(event) => onClick(event, themeId, surveyId)}
      >
        {label}
      </div>
    );
  };

  const getTheme = (themeId, index) => {
    const themeData = draftsData[themeId];
    const label = themeData?.label;

    return (
      <div className={classes.themeStyle} key={index}>
        {'Tēma ' + label?.toUpperCase()}
        {Object.keys(themeData.data)?.map((surveyId, index) =>
          getSurvey(surveyId, index, themeData.data, themeId)
        )}
      </div>
    );
  };

  return (
    <div>
      <Dialog
        fullScreen={isMobile}
        open={true}
        onClose={onClose}
        className={classes.dialogContainerStyle}
      >
        <CloseIcon className={classes.closeButtonStyle} onClick={onClose} />
        <MuiDialogContent className={classes.dialogContentStyle}>
          <div className={classes.titleStyle}>{'Anketu melnraksti'}</div>

          <div className={classes.spanStyle} />

          {draftsData && (
            <div className={classes.fieldsContainerStyle}>
              {Object.keys(draftsData)?.map(getTheme)}
            </div>
          )}

          <div className={classes.spanStyle} />

          {/* <div className={classes.buttonsContainerStyle}>
            <Button
              onClick={onSave}
              isProgress={isLoading}
              text={'SAGLABĀT'}
              disabled={isLoading}
              className={classes.saveButtonStyle}
            />
          </div> */}
        </MuiDialogContent>
      </Dialog>
    </div>
  );
};

export default DraftsDialog;
